<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Monitor from "@/services/Monitor";

const NODE_W = 260;
const NODE_H = 62;
const COL_GAP = 130;
const ROW_GAP = 26;
const PAD = 40;
const ZOOM_STEP = 1.2;

export default {
  components: {
    Layout,
    PageHeader,
  },
  page: {
    title: "Application Features",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      error: null,
      title: "Application Features",
      items: [
        {
          text: "Monitor",
        },
        {
          text: "Application Features",
          active: true,
        },
      ],
      isBusy: false,
      features: [],
      tenants: [],
      selectedTenant: "",
      // key -> active flag for the selected tenant (null = no tenant filter)
      tenantMap: null,
      tenantBusy: false,
      search: "",
      selectedKey: null,
      hoverKey: null,
      // pan / zoom viewBox
      vb: { x: 0, y: 0, w: 0, h: 0 },
      drag: null,
      NODE_W,
      NODE_H,
    };
  },
  computed: {
    byKey() {
      const map = {};
      this.features.forEach((f) => {
        map[f.key] = f;
      });
      return map;
    },
    // key -> layer number (longest dependency chain, roots = 0)
    layerOf() {
      const memo = {};
      const visiting = {};
      const compute = (key) => {
        if (memo[key] != null) return memo[key];
        if (visiting[key]) return 0; // cycle guard
        visiting[key] = true;
        const f = this.byKey[key];
        let layer = 0;
        if (f && Array.isArray(f.depends_on)) {
          f.depends_on.forEach((dep) => {
            if (this.byKey[dep]) layer = Math.max(layer, compute(dep) + 1);
          });
        }
        visiting[key] = false;
        memo[key] = layer;
        return layer;
      };
      this.features.forEach((f) => compute(f.key));
      return memo;
    },
    layout() {
      const layers = {};
      this.features.forEach((f) => {
        const layer = this.layerOf[f.key] || 0;
        (layers[layer] = layers[layer] || []).push(f.key);
      });

      const layerNums = Object.keys(layers).map(Number).sort((a, b) => a - b);
      const rowCounts = layerNums.map((l) => layers[l].length);
      const maxRows = rowCounts.length ? Math.max(...rowCounts) : 1;
      const maxLayer = layerNums.length ? Math.max(...layerNums) : 0;
      const totalH = PAD * 2 + maxRows * (NODE_H + ROW_GAP);
      const totalW = PAD * 2 + (maxLayer + 1) * (NODE_W + COL_GAP) - COL_GAP;

      const pos = {};
      layerNums.forEach((layer) => {
        const keys = layers[layer];
        keys.sort((a, b) => this.byKey[a].name.localeCompare(this.byKey[b].name));
        const colH = keys.length * (NODE_H + ROW_GAP) - ROW_GAP;
        const offset = (totalH - PAD * 2 - colH) / 2;
        keys.forEach((key, i) => {
          pos[key] = {
            x: PAD + layer * (NODE_W + COL_GAP),
            y: PAD + i * (NODE_H + ROW_GAP) + offset,
          };
        });
      });

      return { pos, totalW, totalH };
    },
    nodes() {
      return this.features
        .filter((f) => this.layout.pos[f.key])
        .map((f) => ({ feature: f, ...this.layout.pos[f.key] }));
    },
    edges() {
      const { pos } = this.layout;
      const list = [];
      this.features.forEach((f) => {
        (f.depends_on || []).forEach((dep) => {
          const a = pos[dep];
          const b = pos[f.key];
          if (!a || !b) return;
          const x1 = a.x + NODE_W;
          const y1 = a.y + NODE_H / 2;
          const x2 = b.x;
          const y2 = b.y + NODE_H / 2;
          const mx = (x1 + x2) / 2;
          list.push({
            from: dep,
            to: f.key,
            d: `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`,
          });
        });
      });
      return list;
    },
    viewBox() {
      if (this.vb.w && this.vb.h) {
        return `${this.vb.x} ${this.vb.y} ${this.vb.w} ${this.vb.h}`;
      }
      return `0 0 ${this.layout.totalW} ${this.layout.totalH}`;
    },
    selectedFeature() {
      return this.selectedKey ? this.byKey[this.selectedKey] : null;
    },
    // the key whose relations are highlighted (selection wins over hover)
    activeKey() {
      return this.selectedKey || this.hoverKey;
    },
    relatedKeys() {
      if (!this.activeKey) return null;
      const set = new Set([this.activeKey]);
      const f = this.byKey[this.activeKey];
      if (f) {
        (f.depends_on || []).forEach((k) => set.add(k));
        (f.required_by || []).forEach((k) => set.add(k));
      }
      return set;
    },
    searchLower() {
      return this.search.trim().toLowerCase();
    },
    enabledCount() {
      if (!this.tenantMap) return 0;
      return Object.values(this.tenantMap).filter(Boolean).length;
    },
  },
  watch: {
    async selectedTenant(tenant) {
      this.selectedKey = null;
      if (!tenant) {
        this.tenantMap = null;
        return;
      }
      await this.loadTenantFeatures(tenant);
    },
    features() {
      this.$nextTick(() => this.fit());
    },
    // the graph column switches between col-12 and col-lg-8 when the detail
    // panel opens/closes — refit only on that toggle, not on every reselect
    selectedKey(newKey, oldKey) {
      if (!newKey !== !oldKey) {
        this.$nextTick(() => this.fit());
      }
    },
  },
  async created() {
    await Promise.all([this.getFeatures(), this.getTenants()]);
  },
  mounted() {
    window.addEventListener("mousemove", this.onDragMove);
    window.addEventListener("mouseup", this.onDragEnd);
    window.addEventListener("resize", this.fit);
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onDragMove);
    window.removeEventListener("mouseup", this.onDragEnd);
    window.removeEventListener("resize", this.fit);
  },
  methods: {
    async getFeatures() {
      try {
        this.isBusy = true;
        const response = await Monitor.getFeatures();
        this.features = response.data.data ? response.data.data : [];
        this.error = null;
      } catch (error) {
        this.error =
          error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : "Could not load the feature catalogue";
        this.features = [];
      } finally {
        this.isBusy = false;
      }
    },
    async getTenants() {
      try {
        const response = await Monitor.getTenants();
        const tenants = response.data.tenants ? response.data.tenants : [];
        // MON is the monitor/system tenant, not a real club
        this.tenants = tenants.filter((tenant) => tenant !== "MON");
      } catch (error) {
        this.tenants = [];
      }
    },
    async loadTenantFeatures(tenant) {
      try {
        this.tenantBusy = true;
        const response = await Monitor.getFeatures(`tenant_id=${tenant}`);
        const rows = response.data.data ? response.data.data : [];
        const map = {};
        rows.forEach((row) => {
          map[row.key] = this.rowActiveForTenant(row, tenant);
        });
        this.tenantMap = map;
      } catch (error) {
        this.tenantMap = {};
      } finally {
        this.tenantBusy = false;
      }
    },
    // tolerate both response shapes: a flat `enabled` column from the join,
    // or the assembled `tenants` array
    rowActiveForTenant(row, tenant) {
      if (row.enabled !== undefined && row.enabled !== null) {
        return !!Number(row.enabled);
      }
      if (Array.isArray(row.tenants)) {
        const entry = row.tenants.find((t) => t.tenant_id === tenant);
        return entry ? !!entry.active : false;
      }
      return true;
    },
    isOn(key) {
      if (!this.tenantMap) return true;
      return this.tenantMap[key] === true;
    },
    matchesSearch(f) {
      if (!this.searchLower) return true;
      return (
        f.name.toLowerCase().includes(this.searchLower) ||
        (f.symbol || "").toLowerCase().includes(this.searchLower) ||
        f.key.toLowerCase().includes(this.searchLower)
      );
    },
    nodeClass(f) {
      const cls = [];
      if (!this.isOn(f.key)) cls.push("off");
      if (this.searchLower) {
        if (!this.matchesSearch(f)) cls.push("dim");
      } else if (this.relatedKeys && !this.relatedKeys.has(f.key)) {
        cls.push("dim");
      }
      if (f.key === this.selectedKey) cls.push("selected");
      return cls;
    },
    nodeStroke(f) {
      if (f.type === "core") return "var(--af-core)";
      if (f.type === "minigame") return "var(--af-mini)";
      if (f.package_name) return "var(--af-pkg)";
      if (f.config_fields && f.config_fields.length) return "var(--af-editor)";
      return "var(--af-accent)";
    },
    edgeClass(edge) {
      const cls = [];
      if (!(this.isOn(edge.from) && this.isOn(edge.to))) cls.push("eoff");
      const hi =
        this.activeKey &&
        (edge.from === this.activeKey || edge.to === this.activeKey);
      if (hi) cls.push("hi");
      if ((this.activeKey || this.searchLower) && !hi) cls.push("dim");
      return cls;
    },
    nodeBadges(f) {
      // badges drawn right-to-left inside the node
      const badges = [];
      let x = NODE_W - 18;
      if (f.config_fields && f.config_fields.length) {
        badges.push({ label: "E", fill: "var(--af-editor)", x });
        x -= 20;
      }
      if (f.package_name) {
        badges.push({ label: "P", fill: "var(--af-pkg)", x });
        x -= 20;
      }
      if (f.type === "core") {
        badges.push({ label: "C", fill: "var(--af-core)", x });
        x -= 20;
      }
      if (f.type === "minigame") {
        badges.push({ label: "M", fill: "var(--af-mini)", x });
      }
      return badges;
    },
    select(key) {
      if (!this.byKey[key]) return;
      this.selectedKey = key;
    },
    clearSelection() {
      this.selectedKey = null;
    },
    fit() {
      const wrap = this.$refs.graphWrap;
      const { totalW, totalH } = this.layout;
      if (!wrap || !totalW || !totalH) return;
      const rect = wrap.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const ar = rect.width / rect.height;
      const gar = totalW / totalH;
      let w;
      let h;
      if (gar > ar) {
        w = totalW;
        h = totalW / ar;
      } else {
        h = totalH;
        w = totalH * ar;
      }
      this.vb = { x: (totalW - w) / 2, y: (totalH - h) / 2, w, h };
    },
    onDragStart(e) {
      if (!this.vb.w) this.fit();
      this.drag = { x: e.clientX, y: e.clientY, vx: this.vb.x, vy: this.vb.y };
    },
    onDragMove(e) {
      if (!this.drag) return;
      const wrap = this.$refs.graphWrap;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      this.vb = {
        ...this.vb,
        x: this.drag.vx - ((e.clientX - this.drag.x) / rect.width) * this.vb.w,
        y: this.drag.vy - ((e.clientY - this.drag.y) / rect.height) * this.vb.h,
      };
    },
    onDragEnd() {
      this.drag = null;
    },
    // zoom keeping the given viewport point (0-1 fractions) fixed
    zoomBy(factor, fx = 0.5, fy = 0.5) {
      if (!this.vb.w) this.fit();
      const { totalW } = this.layout;
      const px = this.vb.x + fx * this.vb.w;
      const py = this.vb.y + fy * this.vb.h;
      let w = this.vb.w * factor;
      let h = this.vb.h * factor;
      // clamp between 4x zoomed-in and 3x zoomed-out of the diagram width
      const minW = Math.max(totalW / 8, NODE_W);
      const maxW = totalW * 3;
      if (w < minW) {
        h *= minW / w;
        w = minW;
      }
      if (w > maxW) {
        h *= maxW / w;
        w = maxW;
      }
      this.vb = {
        x: px - fx * w,
        y: py - fy * h,
        w,
        h,
      };
    },
    onWheel(e) {
      const wrap = this.$refs.graphWrap;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      this.zoomBy(
        e.deltaY > 0 ? ZOOM_STEP : 1 / ZOOM_STEP,
        (e.clientX - rect.left) / rect.width,
        (e.clientY - rect.top) / rect.height
      );
    },
    zoomIn() {
      this.zoomBy(1 / ZOOM_STEP);
    },
    zoomOut() {
      this.zoomBy(ZOOM_STEP);
    },
    resetView() {
      this.selectedKey = null;
      this.hoverKey = null;
      this.search = "";
      this.selectedTenant = "";
      this.tenantMap = null;
      this.fit();
    },
    typeLabel(f) {
      if (f.type === "core") return "CORE / LOCKED";
      if (f.type === "minigame") return "MINIGAME";
      return "STANDARD";
    },
    activeTenantsOf(f) {
      return (f.tenants || []).filter((t) => t.active);
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

        <div class="card">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-sm-12 col-md-3 mb-2">
                <label class="mb-1">Tenant</label>
                <b-form-select v-model="selectedTenant" class="form-select">
                  <option value="">All tenants (full catalogue)</option>
                  <option v-for="tenant in tenants" :key="tenant" :value="tenant">
                    {{ tenant }}
                  </option>
                </b-form-select>
              </div>
              <div class="col-sm-12 col-md-4 mb-2">
                <label class="mb-1">Search</label>
                <b-form-input
                  v-model="search"
                  type="search"
                  placeholder="Filter by name, key or symbol..."
                ></b-form-input>
              </div>
              <div class="col-sm-12 col-md-3 mb-2">
                <button class="btn btn-outline-secondary mt-4" @click="resetView">
                  <i class="mdi mdi-restore me-1"></i>Reset view
                </button>
              </div>
              <div class="col-sm-12 col-md-2 mb-2 text-md-end">
                <span v-if="tenantBusy" class="text-muted">
                  <b-spinner small class="me-1"></b-spinner>Loading tenant...
                </span>
                <span v-else-if="tenantMap" class="badge bg-primary font-size-12">
                  {{ selectedTenant }}: {{ enabledCount }} / {{ features.length }} enabled
                </span>
                <span v-else class="badge bg-light text-dark font-size-12">
                  {{ features.length }} features
                </span>
              </div>
            </div>
            <div class="text-muted font-size-11">
              <span class="af-legend af-legend-core"></span> Core
              <span class="af-legend af-legend-mini ms-2"></span> Minigame
              <span class="af-legend af-legend-pkg ms-2"></span> Requires package
              <span class="af-legend af-legend-editor ms-2"></span> Requires config fields
              <span class="ms-3">Drag to pan · Scroll or use the buttons to zoom · Click a node for details</span>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Graph -->
          <div :class="selectedFeature ? 'col-lg-8' : 'col-12'">
            <div class="card">
              <div class="card-body p-2">
                <div v-if="isBusy" class="text-center my-5">
                  <b-spinner class="align-middle me-2"></b-spinner>
                  <strong>Loading feature catalogue...</strong>
                </div>
                <div v-show="!isBusy" class="af-graph-outer">
                  <div class="af-zoom-controls">
                    <button class="btn btn-sm btn-light" title="Zoom in" @click="zoomIn">
                      <i class="mdi mdi-plus"></i>
                    </button>
                    <button class="btn btn-sm btn-light" title="Zoom out" @click="zoomOut">
                      <i class="mdi mdi-minus"></i>
                    </button>
                    <button class="btn btn-sm btn-light" title="Fit to view" @click="fit">
                      <i class="mdi mdi-fit-to-page-outline"></i>
                    </button>
                  </div>
                  <div
                    ref="graphWrap"
                    class="af-graph-wrap"
                    :class="{ grabbing: !!drag }"
                    @mousedown="onDragStart"
                    @wheel.prevent="onWheel"
                  >
                  <svg class="af-svg" :viewBox="viewBox" @click="clearSelection">
                    <defs>
                      <marker
                        id="af-arrow"
                        viewBox="0 0 10 10"
                        refX="9"
                        refY="5"
                        markerWidth="7"
                        markerHeight="7"
                        orient="auto-start-reverse"
                      >
                        <path d="M0,0 L10,5 L0,10 z" fill="var(--af-edge)" />
                      </marker>
                      <marker
                        id="af-arrow-hi"
                        viewBox="0 0 10 10"
                        refX="9"
                        refY="5"
                        markerWidth="7"
                        markerHeight="7"
                        orient="auto-start-reverse"
                      >
                        <path d="M0,0 L10,5 L0,10 z" fill="var(--af-edge-hi)" />
                      </marker>
                      <clipPath id="af-node-clip">
                        <rect :width="NODE_W" :height="NODE_H" />
                      </clipPath>
                    </defs>

                    <path
                      v-for="(edge, i) in edges"
                      :key="'e' + i"
                      class="af-edge"
                      :class="edgeClass(edge)"
                      :d="edge.d"
                      :marker-end="
                        edgeClass(edge).includes('hi')
                          ? 'url(#af-arrow-hi)'
                          : 'url(#af-arrow)'
                      "
                    />

                    <g
                      v-for="node in nodes"
                      :key="node.feature.key"
                      class="af-node"
                      :class="nodeClass(node.feature)"
                      :transform="`translate(${node.x},${node.y})`"
                      clip-path="url(#af-node-clip)"
                      @click.stop="select(node.feature.key)"
                      @mouseenter="hoverKey = node.feature.key"
                      @mouseleave="hoverKey = null"
                    >
                      <rect
                        :width="NODE_W"
                        :height="NODE_H"
                        :fill="
                          node.feature.type === 'core'
                            ? 'var(--af-core-bg)'
                            : 'var(--af-node-bg)'
                        "
                        :stroke="nodeStroke(node.feature)"
                      />
                      <text class="af-title" x="14" y="26">
                        {{ node.feature.name }}
                      </text>
                      <text class="af-sym" x="14" y="46">
                        {{ node.feature.symbol }}
                      </text>
                      <template v-for="badge in nodeBadges(node.feature)">
                        <circle
                          :key="'bc' + badge.label"
                          :cx="badge.x"
                          cy="15"
                          r="8"
                          :fill="badge.fill"
                        />
                        <text
                          :key="'bt' + badge.label"
                          class="af-badge"
                          :x="badge.x"
                          y="19"
                          text-anchor="middle"
                        >
                          {{ badge.label }}
                        </text>
                      </template>
                    </g>
                  </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Detail panel (only while a feature is selected) -->
          <div v-if="selectedFeature" class="col-lg-4">
            <div class="card">
              <div class="card-body">
                <template>
                  <div class="d-flex align-items-start justify-content-between">
                    <h4 class="card-title mb-2">{{ selectedFeature.name }}</h4>
                    <button
                      type="button"
                      class="btn-close"
                      @click="clearSelection"
                    ></button>
                  </div>

                  <div class="mb-3">
                    <span
                      v-if="tenantMap"
                      class="badge me-1"
                      :class="isOn(selectedFeature.key) ? 'bg-success' : 'bg-danger'"
                    >
                      {{ isOn(selectedFeature.key) ? "ENABLED" : "DISABLED" }}
                      · {{ selectedTenant }}
                    </span>
                    <span
                      class="badge me-1"
                      :class="{
                        'bg-warning text-dark': selectedFeature.type === 'core',
                        'bg-info': selectedFeature.type === 'minigame',
                        'bg-secondary': selectedFeature.type === 'standard',
                      }"
                    >
                      {{ typeLabel(selectedFeature) }}
                    </span>
                    <span class="badge bg-light text-dark font-monospace">
                      {{ selectedFeature.symbol }}
                    </span>
                  </div>

                  <h6 class="text-muted text-uppercase font-size-11">Description</h6>
                  <p class="mb-3">
                    {{ selectedFeature.description || "No description provided." }}
                  </p>

                  <template v-if="selectedFeature.package_name">
                    <h6 class="text-muted text-uppercase font-size-11">
                      Required package
                    </h6>
                    <p class="mb-3 font-monospace font-size-12">
                      {{ selectedFeature.package_name }}<br />
                      {{ selectedFeature.package_symbol }}
                    </p>
                  </template>

                  <template
                    v-if="
                      selectedFeature.config_fields &&
                      selectedFeature.config_fields.length
                    "
                  >
                    <h6 class="text-muted text-uppercase font-size-11">
                      Config fields to fill in
                    </h6>
                    <ul class="list-unstyled mb-3">
                      <li
                        v-for="field in selectedFeature.config_fields"
                        :key="field.key"
                        class="mb-1"
                      >
                        <i class="uil uil-edit-alt me-1 text-primary"></i>
                        {{ field.label }}
                        <span v-if="field.required" class="text-danger">*</span>
                      </li>
                    </ul>
                  </template>

                  <h6 class="text-muted text-uppercase font-size-11">Depends on</h6>
                  <ul v-if="selectedFeature.depends_on && selectedFeature.depends_on.length" class="list-unstyled mb-3">
                    <li
                      v-for="dep in selectedFeature.depends_on"
                      :key="dep"
                      class="af-link mb-1"
                      @click="select(dep)"
                    >
                      <i class="uil uil-arrow-up-left me-1"></i>
                      {{ byKey[dep] ? byKey[dep].name : dep }}
                    </li>
                  </ul>
                  <p v-else class="text-muted mb-3">None — this is a root feature.</p>

                  <h6 class="text-muted text-uppercase font-size-11">Required by</h6>
                  <ul v-if="selectedFeature.required_by && selectedFeature.required_by.length" class="list-unstyled mb-3">
                    <li
                      v-for="dep in selectedFeature.required_by"
                      :key="dep"
                      class="af-link mb-1"
                      @click="select(dep)"
                    >
                      <i class="uil uil-arrow-down-right me-1"></i>
                      {{ byKey[dep] ? byKey[dep].name : dep }}
                    </li>
                  </ul>
                  <p v-else class="text-muted mb-3">
                    Nothing depends on this feature.
                  </p>

                  <h6 class="text-muted text-uppercase font-size-11">
                    Tenant availability
                  </h6>
                  <p class="mb-1">
                    Active in
                    <b>{{ activeTenantsOf(selectedFeature).length }}</b>
                    tenant(s).
                  </p>
                  <div>
                    <span
                      v-for="t in selectedFeature.tenants"
                      :key="t.tenant_id"
                      class="badge me-1 mb-1"
                      :class="t.active ? 'bg-success' : 'bg-danger'"
                    >
                      {{ t.tenant_id }}
                    </span>
                    <span
                      v-if="!selectedFeature.tenants || !selectedFeature.tenants.length"
                      class="text-muted"
                    >
                      Not assigned to any tenant.
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.af-graph-outer {
  position: relative;
}

.af-zoom-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.af-zoom-controls .btn {
  border: 1px solid #e2e5ec;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.af-graph-wrap {
  --af-accent: #5b73e8;
  --af-core: #f1b44c;
  --af-core-bg: #fdf3e0;
  --af-mini: #50a5f1;
  --af-pkg: #f46a6a;
  --af-editor: #34c38f;
  --af-edge: #b8c2d0;
  --af-edge-hi: #5b73e8;
  --af-node-bg: #ffffff;

  width: 100%;
  height: 78vh;
  overflow: hidden;
  cursor: grab;
  border: 1px solid #eff2f7;
  border-radius: 4px;
  background: #fbfbfd;
  user-select: none;
}

.af-graph-wrap.grabbing {
  cursor: grabbing;
}

.af-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.af-node {
  cursor: pointer;
}

.af-node rect {
  stroke-width: 1.5;
  rx: 4;
  transition: opacity 0.15s;
}

.af-node .af-title {
  font-size: 15px;
  font-weight: 600;
  fill: #343a40;
}

.af-node .af-sym {
  font-size: 11px;
  font-family: monospace;
  fill: #74788d;
}

.af-node .af-badge {
  font-size: 10px;
  font-weight: 700;
  fill: #ffffff;
  pointer-events: none;
}

.af-node.dim {
  opacity: 0.18;
}

.af-node.off {
  opacity: 0.28;
}

.af-node.off rect {
  stroke-dasharray: 4 3;
}

.af-node.selected rect {
  stroke-width: 3;
}

.af-edge {
  fill: none;
  stroke: var(--af-edge);
  stroke-width: 1.4;
  transition: opacity 0.15s;
}

.af-edge.hi {
  stroke: var(--af-edge-hi);
  stroke-width: 2.2;
}

.af-edge.dim {
  opacity: 0.12;
}

.af-edge.eoff {
  opacity: 0.15;
  stroke-dasharray: 4 3;
}

.af-link {
  cursor: pointer;
  color: #5b73e8;
}

.af-link:hover {
  text-decoration: underline;
}

.af-legend {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  vertical-align: middle;
}

.af-legend-core {
  background: #f1b44c;
}

.af-legend-mini {
  background: #50a5f1;
}

.af-legend-pkg {
  background: #f46a6a;
}

.af-legend-editor {
  background: #34c38f;
}
</style>
